import { createContext, ReactNode, useState } from 'react'
import { useMe } from '../hooks/useMe'
import { isAdult } from '../utils/age-verification'

/**
 * センシティブコンテンツの表示履歴を管理するContext
 *
 * セッション中に一度「表示する」ボタンを押したセンシティブコンテンツは、
 * 再度オーバーレイを表示せずに直接表示する
 */

type SensitiveContentMemoryType = {
  shouldShowContent: (share_id: string | undefined, is_sensitive: boolean, owner_user_id?: string) => boolean
  markAsShown: (share_id: string) => void
  clearAll: () => void
}

export const SensitiveContentMemoryContext = createContext<SensitiveContentMemoryType | undefined>(undefined)

export const SensitiveContentMemoryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const me = useMe()
  // 表示済みの共有アルバムIDをSetで管理（重複排除のため）
  const [shown_shares, setShownShares] = useState<Set<string>>(new Set())

  // センシティブコンテンツを表示すべきかどうかを判定（メモ化は React Compiler に任せる）
  const shouldShowContent = (share_id: string | undefined, is_sensitive: boolean, owner_user_id?: string) => {
    // share_idがundefinedの場合は常に表示（MyAlbumの写真など）
    if (share_id === undefined) {
      return true
    }

    // 自分が所有する共有アルバムの場合は常に表示（センシティブフィルターをスキップ）
    if (owner_user_id && me?.user_id === owner_user_id) {
      return true
    }

    // センシティブでない場合は常に表示
    if (!is_sensitive) {
      return true
    }

    const is_adult = isAdult(me?.birth_date ? new Date(me.birth_date) : undefined)

    // 18歳以上でグローバルフィルターがOFFの場合は常に表示
    if (is_adult && me?.global_sensitive_filter_enabled === false) {
      return true
    }

    // 18歳以上で表示履歴がある場合は表示
    if (is_adult && shown_shares.has(share_id)) {
      return true
    }

    // それ以外はオーバーレイを表示
    return false
  }

  const markAsShown = (share_id: string) => {
    setShownShares(prev => new Set([...prev, share_id]))
  }

  // ログアウト時などに使用
  const clearAll = () => {
    setShownShares(new Set())
  }

  return (
    <SensitiveContentMemoryContext value={{
      shouldShowContent,
      markAsShown,
      clearAll
    }}>
      {children}
    </SensitiveContentMemoryContext>
  )
}